import axios from "axios";

function isLoopbackHostname(hostname) {
  return hostname === "localhost" || hostname === "127.0.0.1" || hostname === "::1";
}

function resolveApiBase() {
  const configured = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

  // Never call the clinician's localhost:8001.
  // sandbox.emrbymd.com is same-origin /api → nginx → SANDBOX backend.
  // Cloud SANDBOX preview only publishes the frontend port; CRA may bake
  // REACT_APP_BACKEND_URL=http://localhost:8001 (the user's machine).
  if (typeof window !== "undefined") {
    if (!configured) return "/api";
    try {
      const backend = new URL(configured, window.location.origin);
      const pagePort =
        window.location.port ||
        (window.location.protocol === "https:" ? "443" : "80");
      const backendPort =
        backend.port || (backend.protocol === "https:" ? "443" : "80");
      const samePublishedHost =
        backend.hostname === window.location.hostname &&
        !isLoopbackHostname(backend.hostname) &&
        backendPort !== pagePort;
      // docker-aws: http://EIP:3001 → http://EIP:8002 (no proxy needed).
      if (samePublishedHost) return `${configured}/api`;
    } catch {
      // Fall through to same-origin /api.
    }
    return "/api";
  }

  return configured ? `${configured}/api` : "/api";
}

export const API = resolveApiBase();

export const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("emr_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  if (typeof window !== "undefined" && config.baseURL) {
    try {
      const backend = new URL(config.baseURL, window.location.origin);
      if (
        isLoopbackHostname(backend.hostname) ||
        backend.origin === window.location.origin
      ) {
        config.baseURL = "/api";
      }
    } catch {
      // keep configured baseURL
    }
  }
  return config;
});

export const ORG_TYPE_SYSTEM =
  "http://terminology.hl7.org/CodeSystem/organization-type";
export const V2_0203 = "http://terminology.hl7.org/CodeSystem/v2-0203";
export const NPI_SYSTEM = "http://hl7.org/fhir/sid/us-npi";
export const TAX_SYSTEM = "urn:oid:2.16.840.1.113883.4.4";
export const CLIA_SYSTEM = "urn:oid:2.16.840.1.113883.4.7";
export const CONTACTENTITY_SYSTEM =
  "http://terminology.hl7.org/CodeSystem/contactentity-type";

export const ORG_TYPES = [
  { code: "prov", display: "Healthcare Provider" },
  { code: "dept", display: "Hospital Department" },
  { code: "team", display: "Organizational Team" },
  { code: "govt", display: "Government" },
  { code: "ins", display: "Insurance Company" },
  { code: "edu", display: "Educational Institute" },
  { code: "other", display: "Other" },
];

// Flatten a FHIR Organization resource into flat form state
export function resourceToForm(r) {
  const idBy = (sys) =>
    (r.identifier || []).find((i) => i.system === sys)?.value || "";
  const tele = (sys) =>
    (r.telecom || []).find((t) => t.system === sys)?.value || "";
  const addr = (r.address && r.address[0]) || {};
  const contact = (r.contact && r.contact[0]) || {};
  return {
    name: r.name || "",
    active: r.active !== false,
    type: r.type?.[0]?.coding?.[0]?.code || "prov",
    npi: idBy(NPI_SYSTEM),
    taxId: idBy(TAX_SYSTEM),
    clia: idBy(CLIA_SYSTEM),
    phone: tele("phone"),
    fax: tele("fax"),
    email: tele("email"),
    line1: addr.line?.[0] || "",
    line2: addr.line?.[1] || "",
    city: addr.city || "",
    state: addr.state || "",
    postalCode: addr.postalCode || "",
    country: addr.country || "US",
    alias: (r.alias || []).join(", "),
    contactName: contact.name?.text || "",
    contactPhone: contact.telecom?.[0]?.value || "",
  };
}

// Build a FHIR-shaped OrganizationInput payload from flat form state
export function formToPayload(f) {
  const identifier = [];
  if (f.npi?.trim())
    identifier.push({
      type: { coding: [{ system: V2_0203, code: "NPI", display: "National provider identifier" }] },
      system: NPI_SYSTEM,
      value: f.npi.trim(),
    });
  if (f.taxId?.trim())
    identifier.push({
      type: { coding: [{ system: V2_0203, code: "TAX", display: "Tax ID number" }] },
      system: TAX_SYSTEM,
      value: f.taxId.trim(),
    });
  if (f.clia?.trim())
    identifier.push({
      type: { coding: [{ system: V2_0203, code: "CLIA", display: "CLIA number" }] },
      system: CLIA_SYSTEM,
      value: f.clia.trim(),
    });

  const telecom = [];
  if (f.phone?.trim()) telecom.push({ system: "phone", value: f.phone.trim(), use: "work" });
  if (f.fax?.trim()) telecom.push({ system: "fax", value: f.fax.trim(), use: "work" });
  if (f.email?.trim()) telecom.push({ system: "email", value: f.email.trim(), use: "work" });

  const line = [f.line1, f.line2].map((s) => s?.trim()).filter(Boolean);
  const hasAddr = line.length || f.city || f.state || f.postalCode;
  const address = hasAddr
    ? [{
        use: "work",
        type: "both",
        ...(line.length ? { line } : {}),
        ...(f.city?.trim() ? { city: f.city.trim() } : {}),
        ...(f.state?.trim() ? { state: f.state.trim() } : {}),
        ...(f.postalCode?.trim() ? { postalCode: f.postalCode.trim() } : {}),
        ...(f.country?.trim() ? { country: f.country.trim() } : {}),
      }]
    : undefined;

  const typeDisplay = ORG_TYPES.find((t) => t.code === f.type)?.display;
  const type = f.type
    ? [{ coding: [{ system: ORG_TYPE_SYSTEM, code: f.type, display: typeDisplay }] }]
    : undefined;

  const alias = f.alias
    ? f.alias.split(",").map((s) => s.trim()).filter(Boolean)
    : undefined;

  const contact = f.contactName?.trim() || f.contactPhone?.trim()
    ? [{
        purpose: { coding: [{ system: CONTACTENTITY_SYSTEM, code: "ADMIN", display: "Administrative" }] },
        ...(f.contactName?.trim() ? { name: { text: f.contactName.trim() } } : {}),
        ...(f.contactPhone?.trim() ? { telecom: [{ system: "phone", value: f.contactPhone.trim(), use: "work" }] } : {}),
      }]
    : undefined;

  return {
    identifier,
    active: !!f.active,
    name: f.name?.trim(),
    ...(type ? { type } : {}),
    ...(alias && alias.length ? { alias } : {}),
    ...(telecom.length ? { telecom } : {}),
    ...(address ? { address } : {}),
    ...(contact ? { contact } : {}),
  };
}
